import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ContentCompetency,
  ContentNugget,
  DeepDive,
  Tool,
  toolTransform,
  deepDiveTransform,
  competencyTransform,
  playlistTransform,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { RESOURCE_KEY, ResourceKey } from '@backrs_ui/grow-ui/src/models/models';
import {
  DashboardFeaturedItem,
  useGetDashboardFeaturedResourcesQuery,
  useGetGuestDashboardFeaturedResourcesQuery,
} from '@backrs_ui/grow-ui/src/redux/dashboardFeatured/dashboardFeatured.api';
import { useGetDeepDivesPaginatedQuery } from '@backrs_ui/grow-ui/src/redux/deepDive/deepDive.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useGetToolsPaginatedQuery } from '@backrs_ui/grow-ui/src/redux/tools/tools.api';
import {
  useGetMostRecentTrackAssessmentResultQuery,
  useGetTrackAssessmentHistoryQuery,
} from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';

export type HomeTab = 'FEATURED' | 'TOOLS' | 'COACHES';

const ITEMS_PER_PAGE = 12;

export type HomeTabNuggetItem = Readonly<{
  key: string;
  type: ResourceKey;
  nugget: ContentNugget;
}>;

const RESOURCE_TYPE_TO_KEY: Record<DashboardFeaturedItem['resource_type'], ResourceKey> = {
  content_nugget: RESOURCE_KEY.RESOURCES,
  tool: RESOURCE_KEY.TOOLS,
  suggested_playbook: RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
  deep_dive: RESOURCE_KEY.DEEP_DIVES,
  competency: RESOURCE_KEY.COMPETENCIES,
};

type UseHomeTabNuggetsResult = Readonly<{
  items: readonly HomeTabNuggetItem[];
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}>;

// One seed per page load, shared across all tab instances so RTK Query
// cache keys stay consistent and ordering doesn't change on tab switch.
const SHUFFLE_SEED = Math.floor(Math.random() * 1000000);

export const useHomeTabNuggets = (tab: HomeTab): UseHomeTabNuggetsResult => {
  const user = useAppSelector(selectUser);
  const { isGuest } = useAuth();
  const [page, setPage] = useState(0);
  const accumulatedItems = useRef<HomeTabNuggetItem[]>([]);
  const [items, setItems] = useState<readonly HomeTabNuggetItem[]>([]);

  const range: readonly [number, number] = [page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE - 1];

  const {
    currentData: featuredResultUser,
    isLoading: isLoadingFeaturedUser,
    isFetching: isFetchingFeaturedUser,
  } = useGetDashboardFeaturedResourcesQuery(
    { range, shuffleSeed: SHUFFLE_SEED },
    { skip: tab !== 'FEATURED' || isGuest },
  );
  const {
    currentData: featuredResultGuest,
    isLoading: isLoadingFeaturedGuest,
    isFetching: isFetchingFeaturedGuest,
  } = useGetGuestDashboardFeaturedResourcesQuery(
    { range, shuffleSeed: SHUFFLE_SEED },
    { skip: tab !== 'FEATURED' || !isGuest },
  );
  const featuredResult = isGuest ? featuredResultGuest : featuredResultUser;
  const isLoadingFeatured = isGuest ? isLoadingFeaturedGuest : isLoadingFeaturedUser;
  const isFetchingFeatured = isGuest ? isFetchingFeaturedGuest : isFetchingFeaturedUser;
  const {
    currentData: toolsResult,
    isLoading: isLoadingTools,
    isFetching: isFetchingTools,
  } = useGetToolsPaginatedQuery({ range, shuffleSeed: SHUFFLE_SEED }, { skip: tab !== 'TOOLS' });
  const {
    currentData: deepDivesResult,
    isLoading: isLoadingDeepDives,
    isFetching: isFetchingDeepDives,
  } = useGetDeepDivesPaginatedQuery(
    { range, shuffleSeed: SHUFFLE_SEED },
    { skip: tab !== 'COACHES' },
  );

  const showsChart = tab === 'FEATURED' && user?.has_blueprint && user?.has_track_assessment;
  const { isLoading: isLoadingTrackResult } = useGetMostRecentTrackAssessmentResultQuery(
    undefined,
    { skip: !showsChart },
  );
  const { isLoading: isLoadingTrackHistory } = useGetTrackAssessmentHistoryQuery(undefined, {
    skip: !showsChart,
  });

  const currentResult =
    tab === 'FEATURED' ? featuredResult : tab === 'TOOLS' ? toolsResult : deepDivesResult;

  const pageItems = useMemo(() => {
    if (tab === 'TOOLS' && toolsResult) return mapTools(toolsResult.data);
    if (tab === 'COACHES' && deepDivesResult) return mapDeepDives(deepDivesResult.data);
    if (tab === 'FEATURED' && featuredResult) return mapFeatured(featuredResult.data);

    return [];
  }, [tab, featuredResult, toolsResult, deepDivesResult]);

  useEffect(() => {
    if (page === 0) {
      accumulatedItems.current = [...pageItems];
    } else {
      accumulatedItems.current = [...accumulatedItems.current, ...pageItems];
    }

    setItems(accumulatedItems.current);
  }, [pageItems, page]);

  const total = currentResult?.total ?? 0;
  const hasMore = items.length > 0 && items.length < total && pageItems.length > 0;

  const isFetching =
    (tab === 'FEATURED' && isFetchingFeatured) ||
    (tab === 'TOOLS' && isFetchingTools) ||
    (tab === 'COACHES' && isFetchingDeepDives);

  const isLoading =
    (tab === 'FEATURED' && (isLoadingFeatured || isLoadingTrackResult || isLoadingTrackHistory)) ||
    (tab === 'TOOLS' && isLoadingTools) ||
    (tab === 'COACHES' && isLoadingDeepDives);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((p) => p + 1);
    }
  }, [isFetching, hasMore]);

  return { items, hasMore, isLoading, isLoadingMore: isFetching && page > 0, loadMore };
};

const mapTools = (tools: readonly Tool[]): HomeTabNuggetItem[] =>
  tools.map((tool) => ({
    key: tool.tool_id,
    type: RESOURCE_KEY.TOOLS,
    nugget: toolTransform(tool),
  }));

const mapDeepDives = (deepDives: readonly DeepDive[]): HomeTabNuggetItem[] =>
  deepDives.map((deepDive) => ({
    key: String(deepDive.id),
    type: RESOURCE_KEY.DEEP_DIVES,
    nugget: deepDiveTransform(deepDive),
  }));

const transformFeaturedResource = (item: DashboardFeaturedItem): ContentNugget | null => {
  switch (item.resource_type) {
    case 'content_nugget':
      return item.resource as ContentNugget;
    case 'tool':
      return toolTransform(item.resource as Tool);
    case 'suggested_playbook':
      return playlistTransform(item.resource as PriorityPlaylist);
    case 'deep_dive':
      return deepDiveTransform(item.resource as DeepDive);
    case 'competency':
      return competencyTransform(item.resource as ContentCompetency);
  }
};

const mapFeatured = (featuredResources: readonly DashboardFeaturedItem[]): HomeTabNuggetItem[] =>
  featuredResources.reduce<HomeTabNuggetItem[]>((acc, item) => {
    const type = RESOURCE_TYPE_TO_KEY[item.resource_type];
    const nugget = transformFeaturedResource(item);

    if (type && nugget) {
      acc.push({ key: String(item.id), type, nugget });
    }

    return acc;
  }, []);
